import React, { useEffect, useState } from 'react';
import { Button, Stack, TextField, Typography } from '@mui/material';

import { MediaSourcePicker } from './MediaSourcePicker';
import { MotionControls } from './MotionControls';
import { buildImageToVideo } from './buildRequests';
import type { PanelPrefill, VideoPanelProps } from './panelTypes';
import type { MediaSource } from '../../../services/mediaSourceService';

/** The "Animate this still" hand-off as a chosen source, when it names an asset. */
function sourceFromPrefill(prefill?: PanelPrefill): MediaSource | null {
  if (!prefill?.sourceImageId) return null;
  return {
    assetId: prefill.sourceImageId,
    previewUrl: prefill.sourceImageUrl ?? '',
    label: 'Animating the still from your gallery',
    origin: 'handoff',
  };
}

/** Still → clip. The hero op for the "Animate this still" bridge (A-F7). */
export const ImageToVideoPanel: React.FC<VideoPanelProps> = ({
  controls,
  onControlsChange,
  onSubmit,
  isRendering,
  prefill,
}) => {
  const [prompt, setPrompt] = useState('');
  const [source, setSource] = useState<MediaSource | null>(() => sourceFromPrefill(prefill));
  // A hand-off that names only a URL (no asset) is still animatable, as before.
  const [prefillUrl, setPrefillUrl] = useState(prefill?.sourceImageId ? undefined : prefill?.sourceImageUrl);

  // Adopt a freshly-arrived handoff source.
  useEffect(() => {
    const next = sourceFromPrefill(prefill);
    if (next) setSource(next);
    setPrefillUrl(prefill?.sourceImageId ? undefined : prefill?.sourceImageUrl);
  }, [prefill?.sourceImageId, prefill?.sourceImageUrl]);

  const hasSource = !!source || !!prefillUrl;
  const canSubmit = hasSource && !isRendering;

  return (
    <Stack spacing={1.5}>
      <MediaSourcePicker
        kind="image"
        label="Source still"
        value={source ?? (prefillUrl ? { assetId: '', previewUrl: prefillUrl, label: 'Animating the still from your gallery', origin: 'handoff' } : null)}
        onChange={(next) => { setSource(next); setPrefillUrl(undefined); }}
        disabled={isRendering}
      />
      <TextField
        label="Prompt (optional)"
        placeholder="Guide the motion…"
        value={prompt}
        onChange={e => setPrompt(e.target.value)}
        multiline
        minRows={2}
        size="small"
        fullWidth
      />
      <MotionControls kind="ImageToVideo" value={controls} onChange={onControlsChange} />
      {!hasSource && (
        <Typography variant="caption" color="text.secondary">
          Upload a still or choose one from your library to animate.
        </Typography>
      )}
      <Button
        variant="contained"
        disabled={!canSubmit}
        onClick={() =>
          onSubmit(
            'ImageToVideo',
            buildImageToVideo(
              {
                sourceImageId: source?.assetId || undefined,
                // As before for the gallery hand-off: its URL travels with its id. A picked or uploaded
                // still is sent by id alone; the server resolves it to a signed link.
                sourceImageUrl: source?.origin === 'handoff' ? prefill?.sourceImageUrl : source ? undefined : prefillUrl,
                prompt,
              },
              controls,
            ),
          )
        }
      >
        {isRendering ? 'Rendering…' : 'Animate still'}
      </Button>
    </Stack>
  );
};
